// Ideas and the next design round (Blue Market only): ideas per screen in the round viewer, and the
// "Next round" section on a project page that sends them to Claude.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Menu, Modal, ago, useConfirm, useToast } from '../ui';
import type { ClaudeRun, Idea, NextRound, Project, Questions } from '../types';
import { RichTextarea } from './RichText';
import { ClaudeRunPanel } from './Spec';

const running = (r: ClaudeRun | null | undefined) => r?.status === 'queued' || r?.status === 'running';

export function useNextRound(projectId: string, enabled = true) {
  return useQuery({ queryKey: ['ideas', projectId], queryFn: () => get<NextRound>(`/api/projects/${projectId}/ideas`), enabled });
}

function useIdeaMutations(projectId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => { qc.invalidateQueries({ queryKey: ['ideas', projectId] }); qc.invalidateQueries({ queryKey: ['comments', projectId] }); };
  const onError = (e: Error) => toast(e.message, true);
  return {
    add: useMutation({ mutationFn: (b: { round: number; screen_id: string | null; body: string }) => post<Idea>(`/api/projects/${projectId}/ideas`, b), onSuccess: refresh, onError }),
    update: useMutation({ mutationFn: ({ id, ...b }: { id: string; body?: string; status?: 'open' | 'dismissed' }) => patch<Idea>(`/api/ideas/${id}`, b), onSuccess: refresh, onError }),
    remove: useMutation({ mutationFn: (id: string) => del(`/api/ideas/${id}`), onSuccess: refresh, onError }),
  };
}

function statusBadge(i: Idea) {
  if (i.status === 'done') return <span className="badge ok tiny-badge" title="A Claude run applied it">Applied{i.submitted_round ? ` in v${i.submitted_round + 1}` : ''}</span>;
  if (i.status === 'submitted') return <span className="badge info tiny-badge" title={`In feedback/v${i.submitted_round}.md, waiting for the next round`}>Sent</span>;
  if (i.status === 'dismissed') return <span className="badge tiny-badge">Dismissed</span>;
  return null;
}

function IdeaForm({ projectId, initial = '', placeholder, submitLabel, onSubmit, onCancel }: {
  projectId: string; initial?: string; placeholder: string; submitLabel: string; onSubmit: (body: string) => Promise<unknown>; onCancel?: () => void;
}) {
  const [body, setBody] = useState(initial);
  const [busy, setBusy] = useState(false);
  const uploading = body.includes('![Uploading ');
  const submit = async () => {
    if (!body.trim() || uploading) return;
    setBusy(true);
    try { await onSubmit(body.trim()); setBody(''); } catch { /* toast already shown */ } finally { setBusy(false); }
  };
  return (
    <form className="composer" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <RichTextarea rows={body.length > 80 ? 4 : 2} value={body} onChange={setBody} uploadUrl={`/api/projects/${projectId}/attachments`} imagesOnly
                    placeholder={placeholder} maxLength={5000} autoFocus={!!onCancel}
                    onSubmit={submit} onCancel={onCancel} />
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        {onCancel && <button type="button" className="btn sm ghost" onClick={onCancel}>Cancel</button>}
        <button className="btn sm primary" disabled={busy || uploading || !body.trim()}>{busy && <span className="spin" />}{submitLabel}</button>
      </div>
    </form>
  );
}

function IdeaItem({ idea, projectId, where }: { idea: Idea; projectId: string; where?: React.ReactNode }) {
  const { can } = useAuth();
  const { update, remove } = useIdeaMutations(projectId);
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(false);
  const editable = can('delivery.edit');
  const items = !editable ? [] : idea.status === 'open' ? [
    { label: 'Edit', onClick: () => setEditing(true) },
    { label: 'Dismiss', onClick: () => update.mutate({ id: idea.id, status: 'dismissed' }) },
    { label: 'Delete', danger: true, onClick: async () => { if (await confirm('Delete this idea?', { ok: 'Delete', danger: true })) remove.mutate(idea.id); } },
  ] : idea.status === 'dismissed' ? [
    { label: 'Restore', onClick: () => update.mutate({ id: idea.id, status: 'open' }) },
    { label: 'Delete', danger: true, onClick: () => remove.mutate(idea.id) },
  ] : [];
  return (
    <div className={`idea ${idea.status}`}>
      <div className="row" style={{ gap: 4, alignItems: 'flex-start' }}>
        <div className="row wrap grow" style={{ gap: '2px 6px', minHeight: 26 }}>
          {where}
          {statusBadge(idea)}
          {idea.comment && <span className="badge tiny-badge" title="Turned into an idea from a comment">From {idea.comment.author}{idea.comment.via_link ? ' (review link)' : ''}</span>}
          <span className="muted tiny">{idea.created_by} · {ago(idea.created_at)}</span>
        </div>
        {items.length > 0 && !editing && <Menu label="Idea actions" items={items} />}
      </div>
      {editing
        ? <IdeaForm projectId={projectId} initial={idea.body} placeholder="Edit the idea" submitLabel="Save" onCancel={() => setEditing(false)}
                    onSubmit={async (body) => { await update.mutateAsync({ id: idea.id, body }); setEditing(false); }} />
        : <div className="comment-text rich-text" dangerouslySetInnerHTML={{ __html: idea.body_html }} />}
      {dialog}
    </div>
  );
}

/** Whether an idea shows on this round and screen: where it's filed, or where the comment it came from was left. */
export function ideaShowsOn(i: Idea, round: number, screenId: string | null): boolean {
  return (i.round === round && (i.screen_id ?? null) === screenId)
    || (!!i.comment && i.comment.round === round && (i.comment.screen_id ?? null) === screenId);
}

/** The Ideas tab in the round viewer: ideas for one screen (or the round as a whole) of this round. */
export function IdeasPanel({ projectId, round, screenId, screenTitle }: { projectId: string; round: number; screenId: string | null; screenTitle?: string }) {
  const { can } = useAuth();
  const { data } = useNextRound(projectId);
  const { add } = useIdeaMutations(projectId);
  const [showOld, setShowOld] = useState(false);
  if (!data) return <span className="spin muted" />;
  const mine = data.ideas.filter((i) => ideaShowsOn(i, round, screenId));
  const current = mine.filter((i) => i.status === 'open' || i.status === 'submitted');
  const old = mine.filter((i) => i.status === 'done' || i.status === 'dismissed');
  const latest = data.latest_round === round;
  // An idea made from this round's comment is filed under a later round: say so.
  const filedElsewhere = (i: Idea) => i.round !== round
    ? <span className="muted tiny">From a comment here, for the round after v{i.round} ·</span> : undefined;
  return (
    <div className="stack" style={{ gap: 12 }}>
      <p className="muted tiny" style={{ margin: 0 }}>
        Ideas are changes for the next round. Only Blue Market sees them.
        {!latest && data.latest_round && <> This is an older round: ideas noted here still go into the next round after v{data.latest_round}.</>}
      </p>
      {can('delivery.edit') && (
        <IdeaForm projectId={projectId} placeholder={screenId ? `An idea for ${screenTitle ?? 'this screen'}` : 'An idea for the round as a whole'} submitLabel="Add idea"
                  onSubmit={(body) => add.mutateAsync({ round, screen_id: screenId, body })} />
      )}
      {current.map((i) => <IdeaItem key={i.id} idea={i} projectId={projectId} where={filedElsewhere(i)} />)}
      {!current.length && <p className="muted small" style={{ margin: 0 }}>No ideas waiting{screenId ? ' for this screen' : ''}.</p>}
      {old.length > 0 && <button className="link-btn tiny" style={{ alignSelf: 'flex-start' }} onClick={() => setShowOld(!showOld)}>
        {showOld ? 'Hide' : 'Show'} {old.length} applied or dismissed</button>}
      {showOld && old.map((i) => <IdeaItem key={i.id} idea={i} projectId={projectId} where={filedElsewhere(i)} />)}
    </div>
  );
}

// ---------- the section on a project page ----------

export function NextRoundSection({ project }: { project: Project }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useNextRound(project.id);
  const { data: questions } = useQuery({ queryKey: ['questions', project.id], queryFn: () => get<Questions>(`/api/projects/${project.id}/questions`) });
  const { add } = useIdeaMutations(project.id);
  const { confirm, dialog } = useConfirm();
  const [sending, setSending] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['ideas', project.id] });
  const submit = useMutation({
    mutationFn: () => post<{ submitted: number; file: string }>(`/api/projects/${project.id}/ideas/submit`),
    onSuccess: (r) => { refresh(); toast(`Added ${r.submitted} idea${r.submitted === 1 ? '' : 's'} to ${r.file}`); },
    onError: (e: Error) => toast((e as Error).message, true),
  });
  const groups = useMemo(() => {
    const open = (data?.ideas ?? []).filter((i) => i.status === 'open' || i.status === 'submitted');
    const by = new Map<string, Idea[]>();
    for (const i of open) { const k = i.screen_id ?? ''; by.set(k, [...(by.get(k) ?? []), i]); }
    const order = ['', ...Object.keys(data?.screens ?? {})];
    return [...by.entries()].sort(([a], [b]) => (order.indexOf(a) + 1 || 999) - (order.indexOf(b) + 1 || 999));
  }, [data]);
  if (!data || !data.latest_round) return null;
  const n = data.latest_round;
  const open = data.ideas.filter((i) => i.status === 'open');
  const done = data.ideas.filter((i) => i.status === 'done').slice(0, 20);
  const canRun = can('claude.run') && can('delivery.edit');
  const busy = running(data.run) || !!data.busy;
  const nothing = !open.length && !data.pending_batches;
  const blocked = data.next_exists ? `v${n + 1} already exists` : busy ? (data.busy && data.busy !== 'design-review' ? `Claude is working on the ${data.busy}` : 'Claude is working on it') : nothing ? 'Add an idea first' : '';

  const submitOnly = async () => {
    if (await confirm(`Add ${open.length} idea${open.length === 1 ? '' : 's'} to feedback/v${n}.md?`, {
      body: 'For running /design-review from a terminal. Send to Claude does this for you.', ok: 'Add to file' })) submit.mutate();
  };

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>Next round</h2>
        <span className="muted tiny">v{n} → v{n + 1}</span>
        <span className="grow" />
        {can('delivery.edit') && open.length > 0 && !running(data.run) && <Menu label="More" items={[{ label: `Add to feedback/v${n}.md only`, onClick: submitOnly }]} />}
      </div>
      <div style={{ padding: '12px 18px' }} className="stack">
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="small grow" style={{ minWidth: 200 }}>
            {open.length ? <><b>{open.length} idea{open.length === 1 ? '' : 's'}</b> waiting</> : 'No ideas waiting'}
            {data.pending_batches > 0 && <span className="muted"> · {data.pending_batches} batch{data.pending_batches === 1 ? '' : 'es'} already in {data.feedback_file}</span>}
            {questions && questions.open > 0 && <> · <Link to={`/projects/${project.id}?tab=questions`}>{questions.open} open question{questions.open === 1 ? '' : 's'}</Link></>}
          </span>
          {can('delivery.edit') && <button className="btn sm ghost" onClick={() => setAdding(!adding)}>Add an idea</button>}
          {canRun && <button className="btn sm primary" disabled={!!blocked} title={blocked} onClick={() => setSending(true)}>Send to Claude</button>}
        </div>
        {adding && <IdeaForm projectId={project.id} placeholder={`An idea for the round as a whole (or add one on a screen in the v${n} viewer)`} submitLabel="Add idea"
                             onCancel={() => setAdding(false)} onSubmit={async (body) => { await add.mutateAsync({ round: n, screen_id: null, body }); setAdding(false); }} />}
        {data.next_exists && !running(data.run) && <p className="muted tiny" style={{ margin: 0 }}>Round v{n + 1} is on disk but hasn't been picked up as the latest yet. Refresh, or check designs/v{n + 1}.</p>}
        {groups.map(([sid, ideas]) => (
          <div key={sid || 'general'}>
            <div className="eyebrow" style={{ marginBottom: 4 }}>
              {sid ? <Link to={`/projects/${project.id}/designs/${n}?screen=${encodeURIComponent(sid)}`}>{data.screens[sid] ?? sid}</Link> : 'Whole round'}
            </div>
            {ideas.map((i) => <IdeaItem key={i.id} idea={i} projectId={project.id}
                                        where={i.round !== n ? <span className="badge tiny-badge">noted on v{i.round}</span> : undefined} />)}
          </div>
        ))}
        {done.length > 0 && <button className="link-btn tiny" style={{ alignSelf: 'flex-start' }} onClick={() => setShowDone(!showDone)}>{showDone ? 'Hide' : 'Show'} recently applied ideas</button>}
        {showDone && done.map((i) => <IdeaItem key={i.id} idea={i} projectId={project.id}
                                             where={<span className="muted tiny">{i.screen_id ? (data.screens[i.screen_id] ?? i.screen_id) : 'Whole round'} ·</span>} />)}
      </div>
      {data.run && <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)' }}><ClaudeRunPanel run={data.run} canStop={canRun} /></div>}
      {sending && <SendDialog project={project} n={n} open={open.length} pending={data.pending_batches} onClose={() => setSending(false)} onStarted={refresh} />}
      {dialog}
    </section>
  );
}

function SendDialog({ project, n, open, pending, onClose, onStarted }: {
  project: Project; n: number; open: number; pending: number; onClose: () => void; onStarted: () => void;
}) {
  const toast = useToast();
  const [instructions, setInstructions] = useState('');
  const start = useMutation({
    mutationFn: () => post<ClaudeRun>(`/api/projects/${project.id}/next-round/claude`, { instructions }),
    onSuccess: () => { onStarted(); onClose(); toast(`Claude is making v${n + 1}`); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={`Make v${n + 1} with Claude`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
        <p className="small" style={{ margin: 0 }}>
          {open > 0 && <>The {open} waiting idea{open === 1 ? '' : 's'} go into feedback/v{n}.md{pending ? `, next to the ${pending} batch${pending === 1 ? '' : 'es'} already there` : ''}. </>}
          {!open && pending > 0 && <>Claude applies the {pending} batch{pending === 1 ? '' : 'es'} waiting in feedback/v{n}.md. </>}
          The platform copies v{n} to v{n + 1}, Claude changes the copy and checks its screenshots, and the platform builds it with a PDF.
          v{n + 1} starts internal: you look at it before sharing. It usually takes 10 to 30 minutes and runs in the background.
        </p>
        <label className="field"><span>Instructions (optional)</span>
          <textarea className="input" rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000}
                    placeholder="e.g. Keep the home screen layout as it is; only change the copy" />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={start.isPending}>{start.isPending && <span className="spin" />}Send to Claude</button>
        </div>
      </form>
    </Modal>
  );
}
